import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISSED_KEY = "fi-radar:pwa-install-dismissed";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function PwaInstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [manualHelp, setManualHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = window.localStorage.getItem(DISMISSED_KEY);
    if (dismissed) return;

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setManualHelp(false);
      setVisible(true);
    };

    const onAppInstalled = () => {
      setInstallEvent(null);
      setVisible(false);
      setManualHelp(false);
      window.localStorage.removeItem(DISMISSED_KEY);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    // Some browsers do not expose beforeinstallprompt. Keep an explicit,
    // non-blocking install affordance so the user can still get instructions.
    const fallbackTimer = window.setTimeout(() => {
      if (!isStandalone()) setVisible(true);
    }, 1500);

    return () => {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (!visible) return null;

  const install = async () => {
    if (!installEvent) {
      setManualHelp(true);
      return;
    }

    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    setInstallEvent(null);
    if (choice.outcome === "accepted") {
      setVisible(false);
      return;
    }
    window.localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  const dismiss = () => {
    window.localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
    setInstallEvent(null);
    setManualHelp(false);
  };

  return (
    <aside className="pwa-install-prompt" role="dialog" aria-label="Install FI Radar">
      <div className="pwa-install-copy">
        <strong>Install FI Radar</strong>
        <span>Get faster access and a dedicated app-like experience.</span>
        {manualHelp ? (
          <span>
            {isIOS()
              ? "On iPhone/iPad: tap Share, then choose Add to Home Screen."
              : "If your browser does not show the native install dialog, open the browser menu and choose Install app or Add to Home screen."}
          </span>
        ) : null}
      </div>
      <div className="pwa-install-actions">
        <button type="button" className="pwa-install-dismiss" onClick={dismiss}>
          Not now
        </button>
        <button type="button" className="pwa-install-button" onClick={() => void install()}>
          {installEvent ? "Install" : "How to install"}
        </button>
      </div>
    </aside>
  );
}
